import get from 'lodash/get';
import { createSelector } from 'reselect';
import { ROLE_CODE_ADMIN, ROLE_CODE_CREATIVE_SPECIALIST, ROLE_CODE_GROUP_MANAGER } from 'common/constants/roles';
import { APP } from '../index';
import { getUser } from '../user/selectors';

export const getUserPermissions = (state) => get(state, [APP, 'userPermissions'], {});

/**
 * Access to the Themes Dashboard
 */
export const getCanUserViewThemesDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.themes?.access,
);

/**
 * Access to the Team Management Top Level Dashboard
 */
export const getCanUserViewTeamDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewUsers?.access || !!userPermissions?.viewGroups?.access,
);

/**
 * Access to view Email Sender
 */
export const getCanUserViewEmailsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewEmailsTab && userPermissions.viewEmailsTab.access,
);

/**
 * Access to edit emails in Email Sender
 */
export const getCanUserEditEmailsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editEmailsTab && userPermissions.editEmailsTab.access,
);

/**
 * Access to create emails in Email Sender
 */
export const getCanUserCreateEmailsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.createEmailsTab && userPermissions.createEmailsTab.access,
);

/**
 * Access to schedule emails in Email Sender
 */
export const getCanUserScheduleEmailsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.scheduleEmail && userPermissions.scheduleEmail.access,
);

/**
 * Access to delete emails in Email Sender
 */
export const getCanUserDeleteEmailsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.deleteEmailsTab && userPermissions.deleteEmailsTab.access,
);

/**
 *  View Email Templates
 */
export const getCanUserViewEmailTemplatesUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewEmailTemplates && userPermissions.viewEmailTemplates.access,
);

/**
 * Access to edit email templates in Email Sender
 */
export const getCanUserEditEmailTemplatesUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editEmailTemplates && userPermissions.editEmailTemplates.access,
);

/**
 * Access to create email Templates in Email Sender
 */
export const getCanUserCreateEmailTemplatesUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.createEmailTemplates && userPermissions.createEmailTemplates.access,
);

/**
 * Access to delete email Templates in Email Sender
 */
export const getCanUserDeleteEmailTemplatesUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.deleteEmailTemplates && userPermissions.deleteEmailTemplates.access,
);

export const getCanUserDeleteGuestsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.deleteGuests && userPermissions.deleteGuests.access,
);

/**
 * Ability to send emails
 */
export const getCanUserSendEmailUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.sendEmail && userPermissions.sendEmail.access,
);

export const getCanUserViewGroupsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewGroups?.access,
);

/**
 * Access to the Reporting Top Level Dashboard
 */
export const getCanUserViewReportingDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.reportingDashboard && userPermissions.reportingDashboard.access,
);

/**
 * Access to the User Dashboard in Team Management
 */
export const getCanUserViewUserDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewUsers?.access,
);

/**
 * Access to the Add Team Users in User Dashboard
 */
export const getCanUserAddTeamMemberUserDashboardUserPermission = createSelector(
    getUserPermissions,
    /**
     * return value can sometimes be undefined as `createUsers` key is not in the store
     * when first called; `createUsers` is added to store later after permissions are requested
     * to avoid react complaining that value is undefined, always return boolean
     */
    (userPermissions) => !!userPermissions?.createUsers?.access,
);

/**
 * Access to the Import Team Users in User Dashboard
 */
export const getCanUserImportTeamMembersUserDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.importUsers?.access,
);

/**
 * Access to the Edit Team Users in User Dashboard
 */
export const getCanUserEditTeamMemberUserDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.editUsers?.access,
);

/**
 * Access to the Delete Team Users in User Dashboard
 */
export const getCanUserDeleteTeamMemberUserDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.deleteUsers?.access,
);

/**
 * =====================
 * INTEGRATIONS
 * ===================
 *
 */

/**
 *  Access to Top level integrations Dashboard
 */
export const getCanUserViewIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewIntegrationsDashboard && userPermissions.viewIntegrationsDashboard.access,
);

/**
 *  Access to Edit Top level integrations
 */
export const getCanUserEditTopLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editIntegrationsDashboard && userPermissions.editIntegrationsDashboard.access,
);

export const getCanUserCreateTopLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.createIntegrationsDashboard && userPermissions.createIntegrationsDashboard.access,
);

export const getCanUserDeleteTopLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.deleteIntegrationsDashboard && userPermissions.deleteIntegrationsDashboard.access,
);

export const getCanUserCreateEventLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.createEventLevelIntegrationsTab && userPermissions.createEventLevelIntegrationsTab.access,
);

export const getCanUserViewEventLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.viewEventLevelIntegrationsTab && userPermissions.viewEventLevelIntegrationsTab.access,
);

export const getCanUserEditEventLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.editEventLevelIntegrationsTab && userPermissions.editEventLevelIntegrationsTab.access,
);

export const getCanUserDeleteEventLevelIntegrationDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.deleteEventLevelIntegrationsTab && userPermissions.deleteEventLevelIntegrationsTab.access,
);

export const getCanUserManageZoomPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.manageZoomEventLevelIntegrationsTab &&
        userPermissions.manageZoomEventLevelIntegrationsTab.access,
);

/*
 * View an event overview tab
 */
export const getCanUserViewEventOverviewUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewEvents && userPermissions.viewEvents.access,
);

/**
 * Edit an event overview page
 */
export const getCanUserEditEventOverviewUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editEvents && userPermissions.editEvents.access,
);

/**
 * Access All Event Touchpoints
 * This controls access to event tab on event level navigation
 */
export const getCanUserViewAllTouchpoints = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewAllTouchpoints && userPermissions.viewAllTouchpoints.access,
);

/**
 * Access to an event touchpoint card
 */
export const getCanUserViewEventPageTouchpointUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewEventPageTouchpoint && userPermissions.viewEventPageTouchpoint.access,
);

/**
 * Edit an event touchpoint
 */
export const getCanUserEditEventPageTouchpointUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editEventPageTouchpoint && userPermissions.editEventPageTouchpoint.access,
);

/**
 * View Access for Confirmation Touchpoint
 */
export const getCanUserViewConfirmationUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.viewConfirmationsTouchpoint && userPermissions.viewConfirmationsTouchpoint.access,
);

/**
 * Edit Access for Confirmation Touchpoint
 */
export const getCanUserEditConfirmationUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        userPermissions.editConfirmationsTouchpoint && userPermissions.editConfirmationsTouchpoint.access,
);

// ==============
// GUESTS
// ==============

export const getCanUserViewGuestListUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewGuests && userPermissions.viewGuests.access,
);

export const getCanUserEditGuestListUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editGuests && userPermissions.editGuests.access,
);

export const getCanUserCreateGuestUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.createGuests && userPermissions.createGuests.access,
);

export const getCanUserExportGuestUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.exportGuests && userPermissions.exportGuests.access,
);

export const getCanUserImportGuestsViaCsvPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.importGuestViaCsv && userPermissions.importGuestViaCsv.access,
);

export const getCanUserShareGuestListUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.shareGuestList && userPermissions.shareGuestList.access,
);

// ==============
// Sessions
// ==============

export const getCanUserViewSessionsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.viewSessionsTab && userPermissions.viewSessionsTab.access),
);

export const getCanUserEditSessionsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.editSessionsTab && userPermissions.editSessionsTab.access),
);

export const getCanUserCreateSessionsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.createSessionsTab && userPermissions.createSessionsTab.access),
);

export const getCanUserDeleteSessionsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.deleteSessionsTab && userPermissions.deleteSessionsTab.access),
);

/**
 * View Contacts dashboard
 */
export const getCanUserViewContactsDashboardUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewContacts && userPermissions.viewContacts.access,
);

// ==============
// EVENT SETTINGS
// ==============

export const getCanUserViewEventSettingsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.viewSettingsTab && userPermissions.viewSettingsTab.access,
);

export const getCanUserEditEventSettingsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editSettingsTab && userPermissions.editSettingsTab.access,
);

/**
 * Edit a theme tab
 */
export const getCanUserEditThemeUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.editThemes && userPermissions.editThemes.access),
);

/**
 * Create a theme
 */
export const getCanUserCreateThemesUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.createThemes && userPermissions.createThemes.access),
);

/**
 * Delete a theme
 */
export const getCanUserDeleteThemesUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.deleteThemes && userPermissions.deleteThemes.access),
);

/**
 * View registration form touchpoint
 */
export const getCanUserViewRegistrationFormUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewRegistrationFormTouchpoint?.access,
);

/**
 * Edit registration form touchpoint
 */
export const getCanUserEditRegistrationFormUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.editRegistrationFormTouchpoint?.access,
);

/**
 * Create registration form touchpoint
 */
export const getCanUserCreateRegistrationFormUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.createRegistrationFormTouchpoint?.access,
);

/**
 * Delete registration form touchpoint
 */
export const getCanUserDeleteRegistrationFormUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.deleteRegistrationFormTouchpoint?.access,
);

/**
 * View tickets tab
 */
export const getCanUserViewTicketsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewTicketsTab?.access,
);

/**
 * Edit tickets tab
 */
export const getCanUserEditTicketsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.editTicketsTab?.access,
);

/**
 * Edit tickets tab loading status
 */
export const getIsFetchingCanUserEditTicketsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.editTicketsTab && userPermissions.editTicketsTab.isLoading),
);

/**
 * Create tickets tab
 */
export const getCanUserCreateTicketsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.createTicketsTab?.access,
);

/**
 * Delete tickets tab
 */
export const getCanUserDeleteTicketsTabUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.deleteTicketsTab?.access,
);

/**
 * Create events
 */
export const getCanUserCreateEventsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.createEvents && userPermissions.createEvents.access),
);

/**
 * Export events
 */
export const getCanUserExportEventsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.exportEvents && userPermissions.exportEvents.access),
);

/**
 * Publish events
 */
export const getCanUserPublishEventsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.publishEvents && userPermissions.publishEvents.access),
);

/**
 * Duplicate events
 */
export const getCanUserDuplicateEventsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.duplicateEvent && userPermissions.duplicateEvent.access),
);

/**
 * View add to hub button
 */
export const getCanUserAddToHubUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.addToHubSection && userPermissions.addToHubSection.access),
);

/**
 * View add to hub button
 */
export const getCanUserDeleteEventsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.deleteEvents && userPermissions.deleteEvents.access),
);

/**
 * View the Block Library tab
 */
export const getCanUserAccessBlockLibraryUserPermission = createSelector(
    getUser,
    (user) => !![ROLE_CODE_ADMIN, ROLE_CODE_CREATIVE_SPECIALIST].includes(user?.roleCode),
);

/**
 * View the Tag Library tab
 */
export const getCanUserAccessTagLibraryUserPermission = createSelector(
    getUser,
    (user) => !![ROLE_CODE_ADMIN, ROLE_CODE_CREATIVE_SPECIALIST].includes(user?.roleCode),
);

/**
 * View the self service Global Brand Manager
 */
export const getCanUserViewGlobalBrandManagerUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewBrands?.access,
);

/**
 * Edit an existing brand in the self service Global Brand Manager
 */
export const getCanUserEditGlobalBrandManagerUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.editBrands && userPermissions.editBrands.access),
);

/**
 * Create a brand in the self service Global Brand Manager
 */
export const getCanUserCreateGlobalBrandManagerUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.createBrands && userPermissions.createBrands.access),
);

/**
 * Publish a brand in the self service Global Brand Manager
 */
export const getCanUserPublishGlobalBrandManagerUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.publishBrands && userPermissions.publishBrands.access),
);

/**
 * Apply forms
 */
export const getCanUserApplyFormsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.applyForms?.access,
);

/**
 * Create library forms
 */
export const getCanUserCreateLibraryFormsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.createLibraryForms?.access,
);

/**
 * Update library forms
 */
export const getCanUserUpdateLibraryFormsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.updateLibraryForms?.access,
);
/**
 * Delete library forms
 */
export const getCanUserDeleteLibraryFormsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.deleteLibraryForms?.access,
);
/**
 * View library forms
 */
export const getCanUserViewLibraryFormsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.viewLibraryForms?.access,
);

/**
 * Access to the Analytics Tab Event Level Dashboard
 */
export const getCanUserAccessAnalyticsUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.analyticsTab && userPermissions.analyticsTab.access),
);

/**
 * Update workflow status
 */
export const getCanUpdateWorkflowStatusUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!(userPermissions.approveEvents && userPermissions.approveEvents.access),
);

/**
 * Access vanity url in Event Settings; if no, then field is read only
 */
export const getCanUserEditVanityUrlUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => userPermissions.editVanityUrl && userPermissions.editVanityUrl.access,
);

/**
 * Access edit url in Event Settings; if no, then field is read only
 */
export const getCanUserEditEventUrlUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) => !!userPermissions?.editEventUrl?.access,
);

/**
 * Access to the Virtual Event Touchpoint
 */
export const getCanUserViewVirtualEventTouchpointUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(userPermissions.viewVirtualEventTouchpoint && userPermissions.viewVirtualEventTouchpoint.access),
);

/**
 * Access to the Virtual Event Touchpoint
 */
export const getCanUserCreateVirtualEventTouchpointUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(userPermissions.createVirtualEventTouchpoint && userPermissions.createVirtualEventTouchpoint.access),
);

/**
 * Access to the Virtual Event Touchpoint
 */
export const getCanUserEditVirtualEventTouchpointUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(userPermissions.editVirtualEventTouchpoint && userPermissions.editVirtualEventTouchpoint.access),
);

/**
 * Access to the Virtual Event Touchpoint
 */
export const getCanUserDeleteVirtualEventTouchpointUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(userPermissions.deleteVirtualEventTouchpoint && userPermissions.deleteVirtualEventTouchpoint.access),
);

/**
 * Access to the Organization Settings Data Anonymization
 */
export const getCanUserAccessDataAnonymizationUserPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(
            userPermissions.accessDataAnonymizationOrganizationSettings &&
            userPermissions.accessDataAnonymizationOrganizationSettings.access
        ),
);

/**
 * Access to the Organization Settings Privacy Management
 */
export const getCanUserAccessPrivacyManagementOrgSettingsPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(
            userPermissions.accessPrivacyManagementOrganizationSettings &&
            userPermissions.accessPrivacyManagementOrganizationSettings.access
        ),
);

/**
 * Access to the Event Settings Privacy Management
 */
export const getCanUserAccessPrivacyManagementEventSettingsPermission = createSelector(
    getUserPermissions,
    (userPermissions) =>
        !!(
            userPermissions.accessPrivacyManagementEventSettings &&
            userPermissions.accessPrivacyManagementEventSettings.access
        ),
);

export const getCanUserAccessCMSCodeEditor = createSelector(
    getUser,
    (user) => !![ROLE_CODE_ADMIN, ROLE_CODE_CREATIVE_SPECIALIST, ROLE_CODE_GROUP_MANAGER].includes(user?.roleCode),
);
